import React from "react";
import { useNavigate } from "react-router-dom";
import type { CenterWithManager, CenterCreate } from "types/domain";
import { centerService } from "services/admin/centerService";
import ActionButton from "components/ui/ActionButton";
import EmptyState from "components/ui/EmptyState";
import SectionHeader from "components/ui/SectionHeader";
import { useLanguage } from "context/LanguageContext";
import CreateCenterModal from "./components/CreateCenterModal";
import { MdAdd, MdArrowForward, MdBusiness, MdPerson } from "react-icons/md";

export default function CenterManagement() {
  const navigate = useNavigate();
  const { t, locale } = useLanguage();
  const [centers, setCenters] = React.useState<CenterWithManager[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [managerFilter, setManagerFilter] = React.useState("");
  const [includeManager, setIncludeManager] = React.useState(true);

  const fetchCenters = React.useCallback(async () => {
    setLoading(true);
    try {
      // API call with optional manager info
      const res = await centerService.getAll({
        limit: 100,
        include_manager: includeManager,
      });
      setCenters(res.items);
    } catch (err) {
      console.error("Failed to load centers:", err);
    } finally {
      setLoading(false);
    }
  }, [includeManager]);

  React.useEffect(() => {
    fetchCenters();
  }, [fetchCenters]);

  const handleCreate = async (data: CenterCreate) => {
    return centerService.create(data);
  };

  const handleCreated = (centerId: string) => {
    navigate(`/systemadmin/centers/${centerId}`);
  };

  return (
    <div className="mt-5 px-4">
      <SectionHeader
        title={t("centers.title")}
        description={t("centers.subtitle")}
        icon={<MdBusiness className="h-5 w-5" />}
        actions={
          <ActionButton
            startIcon={<MdAdd className="h-5 w-5" />}
            onClick={() => setIsModalOpen(true)}
          >
            {t("centers.create.submit")}
          </ActionButton>
        }
      />

      {/* Controls */}
      {!loading && centers.length > 0 && (
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <input
            value={managerFilter}
            onChange={(e) => setManagerFilter(e.target.value)}
            placeholder={t("centers.managerFilterPlaceholder")}
            disabled={!includeManager}
            className="w-full max-w-sm rounded-xl border border-line bg-surface px-4 py-2 text-sm text-ink outline-none focus:border-brand-500 disabled:bg-gray-50 disabled:text-gray-400 dark:disabled:bg-surface-muted/50"
          />
          <label className="flex items-center gap-2 whitespace-nowrap text-sm text-ink">
            <input
              type="checkbox"
              checked={includeManager}
              onChange={(e) => {
                setIncludeManager(e.target.checked);
                setManagerFilter("");
              }}
              className="h-4 w-4 cursor-pointer rounded border-line"
            />
            {t("centers.includeManager")}
          </label>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-brand-500" />
        </div>
      ) : centers.length === 0 ? (
        <EmptyState
          icon={<MdBusiness className="h-14 w-14 opacity-70" />}
          title={t("centers.emptyTitle")}
          description={t("centers.emptyDescription")}
          action={
            <ActionButton
              startIcon={<MdAdd className="h-5 w-5" />}
              onClick={() => setIsModalOpen(true)}
            >
              {t("centers.create.submit")}
            </ActionButton>
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {centers
            .filter((center) => {
              if (!managerFilter.trim()) return true;
              if (!center.managers || center.managers.length === 0)
                return false;
              const keyword = managerFilter.trim().toLowerCase();
              return center.managers.some((m) =>
                m.full_name.toLowerCase().includes(keyword)
              );
            })
            .map((center) => (
              <div
                key={center.id}
                className="group rounded-2xl border border-line bg-surface p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 dark:bg-brand-900/20">
                    <MdBusiness className="h-5 w-5 text-brand-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-ink">
                      {center.name}
                      {center.code && (
                        <span className="ml-2 inline-block rounded bg-brand-50 px-1.5 py-0.5 text-xs font-medium text-brand-600 dark:bg-brand-900/20 dark:text-brand-300">
                          {center.code}
                        </span>
                      )}
                    </p>
                    <p className="mt-0.5 truncate text-sm text-ink-muted">
                      {center.address || t("centers.noAddress")}
                    </p>
                    <p className="mt-2 text-xs text-ink-subtle">
                      {t("centers.createdOn", {
                        date: new Date(center.created_at).toLocaleDateString(
                          locale
                        ),
                      })}
                    </p>
                  </div>
                </div>
                {/* Managers */}
                {includeManager &&
                  center.managers &&
                  center.managers.length > 0 && (
                    <div className="mt-3 border-t border-line pt-3">
                      <p className="mb-1.5 text-xs font-medium text-ink-muted">
                        {t("centers.managersCount", {
                          count: center.managers.length,
                        })}
                      </p>
                      <div className="space-y-1.5">
                        {center.managers.map((manager) => (
                          <div
                            key={manager.id}
                            className="flex items-center gap-2"
                          >
                            <MdPerson className="h-3.5 w-3.5 shrink-0 text-purple-500" />
                            <div className="min-w-0">
                              <p className="truncate text-xs font-medium text-ink">
                                {manager.full_name}
                              </p>
                              <p className="truncate text-xs text-ink-subtle">
                                {manager.email}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                <ActionButton
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/systemadmin/centers/${center.id}`)}
                  className="mt-4 px-2 text-brand-500 hover:bg-brand-50 dark:text-brand-200 dark:hover:bg-brand-500/10"
                  startIcon={<MdArrowForward className="h-4 w-4" />}
                  endIcon={
                    <MdArrowForward className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                  }
                >
                  {t("action.viewDetail")}
                </ActionButton>
              </div>
            ))}
        </div>
      )}

      <CreateCenterModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreate}
        onCreated={handleCreated}
      />
    </div>
  );
}
